import React, { useState, useEffect } from "react";
import {
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Grid,
  Drawer,
  Typography,
  IconButton,
  MenuItem,
  Menu,
} from "@mui/material";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import utc from "dayjs/plugin/utc";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { styled } from "@mui/material/styles";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useMutation } from "@apollo/client";
import { reportExecuteMutation, reportCancelMutation } from "../mutations.js";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { CircularProgress } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import { useReportContext } from "../hooks/useReportContext";

dayjs.extend(utc);
dayjs.extend(weekday);

const isWeekend = (date) => {
  return dayjs(date).weekday() === 0 || dayjs(date).weekday() === 6;
};

const MuiInputStyleOverride = styled(Box)({
  "& .MuiInputBase-root": {
    height: "36px !important",
    display: "flex !important",
    alignItems: "center !important",
    fontSize: "0.875rem !important",
  },
  "& .MuiInputBase-input": {
    padding: "0 14px !important",
    height: "20px !important",
    lineHeight: "20px !important",
  },
  "& .MuiInputLabel-root": {
    top: "50% !important",
    left: "14px !important",
    transform: "translate(0, -50%) scale(1) !important",
    transformOrigin: "top left !important",
    position: "absolute !important",
    zIndex: "1 !important",
    pointerEvents: "none !important",
    fontSize: "0.875rem !important",
    transition:
      "color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms,transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms,max-width 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms !important",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(2px, -6px) scale(0.75) !important",
    transformOrigin: "top left !important",
    fontSize: "0.875rem !important",
    top: "0 !important",
    left: "14px !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.23) !important",
  },
  "& .MuiFormLabel-root": {
    position: "absolute !important",
    top: "50% !important",
    left: "14px !important",
    transform: "translate(0, -50%) scale(1) !important",
    fontSize: "0.875rem !important",
  },
  "& .MuiFormLabel-root.Mui-focused, & .MuiFormLabel-root.MuiFormLabel-filled, & .MuiInputLabel-filled":
    {
      transform: "translate(2px, -6px) scale(0.75) !important",
      top: "0 !important",
      left: "14px !important",
    },
  "& .MuiOutlinedInput-root:not(.Mui-focused) .MuiInputLabel-root:not(.MuiInputLabel-shrink)":
    {
      transform: "translate(0, -50%) scale(1) !important",
    },
  "& .MuiOutlinedInput-root .MuiInputLabel-filled, & .MuiInputLabel-root.MuiInputLabel-filled":
    {
      transform: "translate(2px, -6px) scale(0.75) !important",
      top: "0 !important",
      left: "14px !important",
    },
  "& .MuiInputBase-input:not(:placeholder-shown) ~ .MuiInputLabel-root": {
    transform: "translate(4px, -6px) scale(0.75) !important",
    top: "0 !important",
    left: "14px !important",
  },
  "& .MuiInputBase-root.MuiOutlinedInput-root .MuiInputLabel-root.MuiInputLabel-shrink":
    {
      transform: "translate(2px, -6px) scale(0.75) !important",
      top: "0 !important",
      left: "14px !important",
    },
  "& .MuiTextField-root": {
    position: "relative !important",
  },
  "& .MuiInputBase-formControl": {
    position: "relative !important",
  },
});

const SplitButton = styled(Box)({
  display: "flex",
  height: "100%",
});

const MainButton = styled(Button)({
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  flex: 1,
});

const DropdownButton = styled(Button)(({ theme }) => ({
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  padding: theme.spacing(0, 1),
  minWidth: "40px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
}));

const FormatText = styled(Typography)({
  fontSize: "0.75rem",
  lineHeight: 1,
});

const ActionButton = styled(Button)({
  textTransform: "none",
  fontWeight: 500,
  borderRadius: 4,
});

const ControllerWrapper = styled(Box)({
  position: "relative",
  zIndex: 20,
});

const MainContainer = styled(Box)({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  zIndex: 20,
  backgroundColor: "#fff",
});

const Overlay = styled(Box)({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  bottom: "-1000px",
  backgroundColor: "rgba(0, 0, 0, 0.3)",
  zIndex: 10,
  pointerEvents: "auto",
});

const StyledDrawer = styled(Drawer)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 15,
  pointerEvents: "none",
  "& .MuiDrawer-paper": {
    pointerEvents: "auto",
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    maxHeight: "calc(100vh - 200px)",
    borderRadius: "0 0 8px 8px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    transform: "none",
    zIndex: 15,
  },
});

const DownloadsContainer = styled(Box)({
  maxHeight: "650px",
  overflow: "auto",
  margin: "0px 10px 0px 10px",
  position: "relative",
});

const DownloadsHeader = styled(Box)({
  position: "sticky",
  top: 0,
  backgroundColor: "#fff !important",
  zIndex: 100,
  borderBottom: "2px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  padding: "8px 16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "0",
});

const DownloadsTitle = styled(Typography)({
  fontWeight: "bold",
  fontSize: "1rem",
  margin: 0,
});

const DownloadsContentWrapper = styled(Box)({
  "& .grid-container": {
    display: "grid !important",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr)) !important",
    gap: "12px !important",
    padding: "16px !important",
    background: "transparent !important",
  },
  "& .grid-item": {
    border: "1px solid #e0e0e0 !important",
    padding: "0 !important",
    textAlign: "left !important",
    background: "#fff !important",
    borderRadius: "4px !important",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12) !important",
    transition: "all 0.2s ease !important",
    overflow: "hidden !important",
    position: "relative !important",
    "&:hover": {
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.16) !important",
      borderColor: "#1976d2 !important",
    },
  },
  "& .grid-item a": {
    display: "flex !important",
    padding: "12px 16px !important",
    textDecoration: "none !important",
    color: "#424242 !important",
    fontSize: "13px !important",
    fontWeight: "500 !important",
    lineHeight: "1.3 !important",
    borderLeft: "3px solid #1976d2 !important",
    paddingLeft: "13px !important",
    background: "#fff !important",
    minHeight: "40px !important",
    alignItems: "center !important",
    transition: "all 0.15s ease !important",
    "&:hover": {
      color: "#1976d2 !important",
      background: "#f5f5f5 !important",
    },
    "&:active": {
      background: "#eeeeee !important",
    },
  },
  "@media (max-width: 600px)": {
    "& .grid-container": {
      gridTemplateColumns: "1fr !important",
      padding: "12px !important",
      gap: "8px !important",
    },
  },
});

const DownloadsContent = styled(Box)({
  padding: "0",
  position: "relative",
});

const DownloadsCloseButton = styled(IconButton)({
  borderRadius: "50%",
});

const LoadingOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  background: "rgba(245, 245, 245, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 30,
});

const LoadingContent = styled(Box)({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

const CancelButton = styled(ActionButton)({
  position: "absolute",
  top: "100%",
  marginTop: "0px",
  opacity: 0,
  transition: "opacity 0.3s",
  "&.visible": {
    opacity: 1,
  },
});

const StyledSnackbar = styled(Snackbar)({
  marginTop: 140,
});

const StyledGridContainer = styled(Grid)({
  padding: "15px 30px 15px 15px",
  marginBottom: 0,
});

const CheckboxContainer = styled(Grid)({
  maxWidth: "50px",
});

const CheckboxBox = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  width: "50px",
  marginLeft: "15px",
});

const DatePickerContainer = styled(MuiInputStyleOverride)({
  height: "36px",
});

const ReportingController = ({ selectedRowData, gridBoxRef }) => {
  // Use the report context instead of props
  const {
    reportRunData,
    refetchReportRun,
    executionId,
    clearReportLogs,
    isDownloaLinksAvailable,
    getReportCount,
  } = useReportContext();

  const [isDateActive, setIsDateActive] = useState(false);
  const [format, setFormat] = useState("XLS");
  const [anchorEl, setAnchorEl] = useState(null);

  const startDate = null;
  const [endDate, setEndDate] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const [executingRequest, setExecutingRequest] = useState(false);

  const [hovering, setHovering] = React.useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  const [executionType, setExecutionType] = useState(null);

  const [downloadDrawerOpen, setDownloadDrawerOpen] = useState(false);

  const [executeReport] = useMutation(reportExecuteMutation);
  const [cancelReport] = useMutation(reportCancelMutation);

  useEffect(() => {
    if (isDateActive) {
      const now = dayjs();

      let date = now.subtract(1, "day");
      while (isWeekend(date)) {
        date = date.subtract(1, "day");
      }

      setEndDate(date);
    }
  }, [isDateActive]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleDialogOpen = (message) => {
    setDialogMessage(message);
    setDialogOpen(true);
  };

  const handleFormatMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFormatMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFormatSelect = (selectedFormat) => {
    setFormat(selectedFormat);
    handleFormatMenuClose();
  };

  const handleExecute = async (type, bypass) => {
    setExecutionType(type);
    if (type === "Official" && !bypass && selectedRowData.length === 0) {
      const message =
        "All Active reports marked as Official will be sent to recipients.";
      handleDialogOpen(message);
      return;
    }
    setExecutingRequest(true);
    setIsCanceling(false);
    clearReportLogs();
    const selectedIsins = selectedRowData.map((row) => row.ISIN);
    const ReportExecutionInput = {
      reportType: type,
      isDateActive,
      startDate,
      endDate,
      selectedIsins,
      format,
    };

    try {
      let res = await executeReport({
        variables: { input: ReportExecutionInput },
      });
      if (res.data.executeReport.status !== 200) {
        setSnackbarMessage(
          `Error generating reports: ${res.data.executeReport.statusText}`
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else {
        setSnackbarSeverity("success");
        setSnackbarMessage(
          `${ReportExecutionInput.reportType} Reports are processing. RefId = ${res.data.executeReport.statusText}`
        );
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage(`Error processing reports: ${error.message}`);
      setSnackbarSeverity("error");
      console.error(error.message);
      setSnackbarOpen(true);
    }
    setExecutingRequest(false);
    refetchReportRun();
  };

  const handleCancel = async () => {
    setIsCanceling(true);

    if (executionId !== undefined) {
      await cancelReport({
        variables: { input: executionId },
      });
      refetchReportRun();
    }
  };

  const handleDownloadClick = () => {
    if (isDownloaLinksAvailable()) {
      setDownloadDrawerOpen(true);
    }
  };

  const handleDrawerClose = () => {
    setDownloadDrawerOpen(false);
  };

  const showLoadingOverlay =
    executingRequest || reportRunData?.run?.status === 1;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ControllerWrapper className="controller-wrapper">
        {downloadDrawerOpen && <Overlay onClick={handleDrawerClose} />}

        <MainContainer>
          {showLoadingOverlay && (
            <LoadingOverlay>
              <LoadingContent
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
              >
                <CircularProgress />
                <CancelButton
                  variant="outlined"
                  disabled={isCanceling}
                  className={hovering ? "visible" : ""}
                  onClick={handleCancel}
                >
                  {isCanceling ? "Canceling..." : "Cancel Run"}
                </CancelButton>
              </LoadingContent>
            </LoadingOverlay>
          )}
          <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {"Confirmation Required"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {dialogMessage}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDialogOpen(false)}
                color="primary"
                component={ActionButton}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setDialogOpen(false);
                  handleExecute(executionType, true);
                }}
                color="primary"
                variant="outlined"
                component={ActionButton}
                autoFocus
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
          <StyledSnackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
              {snackbarMessage}
            </Alert>
          </StyledSnackbar>
          <StyledGridContainer container spacing={2} alignItems="center">
            <Grid
              item
              xs={12}
              sm={3}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px", // consistent small space
                flexWrap: "nowrap",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDateActive}
                    onChange={() => setIsDateActive(!isDateActive)}
                  />
                }
                label=""
                sx={{ margin: 0 }}
              />

              <Box sx={{ flex: 1, minWidth: 160 }}>
                <DatePickerContainer>
                  <DatePicker
                    label="Valuation Date"
                    value={endDate}
                    onChange={setEndDate}
                    disabled={!isDateActive}
                    timezone="UTC"
                    fullWidth
                    disableFuture
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "outlined",
                      },
                    }}
                  />
                </DatePickerContainer>
              </Box>
            </Grid>
            <Grid item xs={12} sm={2}>
              <SplitButton>
                <MainButton
                  variant="outlined"
                  color="primary"
                  component={ActionButton}
                  onClick={() => handleExecute("Generate")}
                  disabled={selectedRowData.length === 0}
                >
                  {selectedRowData.length > 0
                    ? `Generate ${selectedRowData.length} Selected Reports`
                    : `Generate Reports`}
                </MainButton>
                <DropdownButton
                  variant="outlined"
                  color="primary"
                  onClick={handleFormatMenuOpen}
                  disabled={selectedRowData.length === 0}
                >
                  <FormatText>{format}</FormatText>
                  <ArrowDropDownIcon fontSize="small" />
                </DropdownButton>
              </SplitButton>
              <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleFormatMenuClose}
              >
                <MenuItem onClick={() => handleFormatSelect("CSV")}>
                  CSV
                </MenuItem>
                <MenuItem onClick={() => handleFormatSelect("XLS")}>
                  XLS
                </MenuItem>
              </Menu>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Tooltip
                title={
                  selectedRowData.length === 0
                    ? "All Active reports marked as Official will be send to recipients"
                    : ""
                }
                arrow
              >
                <ActionButton
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={() => handleExecute("Official")}
                >
                  {selectedRowData.length > 0
                    ? `Send ${selectedRowData.length} Selected Reports`
                    : `Send Reports`}
                </ActionButton>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={2}>
              <ActionButton
                variant="outlined"
                color="primary"
                fullWidth
                disabled={!isDownloaLinksAvailable()}
                onClick={handleDownloadClick}
              >
                {`Download Reports (${
                  isDownloaLinksAvailable() ? getReportCount() : 0
                })`}
              </ActionButton>
            </Grid>
          </StyledGridContainer>
        </MainContainer>

        <StyledDrawer
          anchor="top"
          open={downloadDrawerOpen}
          onClose={handleDrawerClose}
          variant="persistent"
          ModalProps={{
            container: () =>
              document.querySelector(".controller-wrapper") || document.body,
            disableEnforceFocus: true,
            hideBackdrop: true,
          }}
        >
          {isDownloaLinksAvailable() && (
            <DownloadsContainer>
              <DownloadsHeader>
                <DownloadsTitle>Download Reports</DownloadsTitle>
                <DownloadsCloseButton
                  onClick={handleDrawerClose}
                  title="Close downloads"
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </DownloadsCloseButton>
              </DownloadsHeader>
              <DownloadsContentWrapper
                dangerouslySetInnerHTML={{
                  __html: reportRunData.run.downloadLinksHtml,
                }}
              />
            </DownloadsContainer>
          )}
        </StyledDrawer>
      </ControllerWrapper>
    </LocalizationProvider>
  );
};

export default ReportingController;
